import React, { useState, useEffect } from 'react';
import { X, CreditCard, Loader, Check, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { subscriptionService } from '../../services/subscriptionService';

// Card element styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const PaymentForm = ({ onSuccess, onError, currentCard }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    try {
      setLoading(true);
      
      // Get SetupIntent from backend
      const setupIntent = await subscriptionService.createSetupIntent();
      
      // Confirm card setup with Stripe
      const { error, setupIntent: confirmedSetupIntent } = await stripe.confirmCardSetup(
        setupIntent.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      // Update payment method in backend
      await subscriptionService.updatePaymentMethod(confirmedSetupIntent.payment_method);
      
      onSuccess();
    } catch (err) {
      console.error('Error updating payment method:', err);
      onError(err.message || 'Failed to update payment method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Current Card Display */}
      {currentCard && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Current card on file:</p>
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-900">
              {currentCard.brand.charAt(0).toUpperCase() + currentCard.brand.slice(1)} •••• {currentCard.last4}
            </span>
            <span className="text-sm text-gray-600">
              Exp {currentCard.expMonth}/{currentCard.expYear}
            </span>
          </div>
        </div>
      )}

      {/* New Card Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          New Card Details
        </label>
        <div className="border border-gray-300 rounded-lg p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          🔒 Your card information is encrypted and secure
        </p>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {loading ? (
          <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Updating...
          </>
        ) : (
          <>
            <CreditCard className="mr-2 h-5 w-5" />
            Update Payment Method
          </>
        )}
      </button>
    </form>
  );
};

const UpdatePaymentMethodModal = ({ isOpen, onClose, onSuccess, currentCard }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Initialize Stripe with your publishable key
    const initStripe = async () => {
      try {
        const setupIntent = await subscriptionService.createSetupIntent();
        const stripe = await loadStripe(setupIntent.publishableKey);
        setStripePromise(stripe);
      } catch (err) {
        console.error('Error initializing Stripe:', err);
        setError('Failed to initialize payment form');
      }
    };

    if (isOpen) {
      initStripe();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSuccess = () => {
    setSuccess(true);
    setTimeout(() => {
      onSuccess();
      onClose();
      setSuccess(false);
      setError(null);
    }, 2000);
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleClose = () => {
    if (!success) {
      onClose();
      setError(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Update Payment Method</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={success}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* Success State */}
          {success ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Payment Method Updated!
              </h3>
              <p className="text-gray-600">
                Your new card has been saved successfully.
              </p>
            </div>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Payment Form */}
              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <PaymentForm
                    onSuccess={handleSuccess}
                    onError={handleError}
                    currentCard={currentCard}
                  />
                </Elements>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <Loader className="h-8 w-8 text-blue-600 animate-spin" />
                  <span className="ml-3 text-gray-600">Loading payment form...</span>
                </div>
              )}

              {/* Cancel Button */}
              {stripePromise && (
                <button
                  onClick={handleClose}
                  className="w-full mt-3 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdatePaymentMethodModal;